import React, { useEffect, useMemo, useState } from 'react';
import ReactSpeedometer from 'react-d3-speedometer';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import Chart from 'react-apexcharts';
import axios from 'axios';
import BaseURL from '../../../assets/contants/BaseURL';
import useLiveValue from '../useLiveValue';
import useMeterField from '../useMeterField';

const GaugeWidget = ({ config }) => {
    const { dataSource = {}, settings = {}, variant = 'speedometer', title } = config;
    const max = settings.max ?? 100;
    const [fetchedValue, setFetchedValue] = useState(null);
    const isPreview = typeof config.id === 'string' && config.id.startsWith('preview-');
    const report = useMeterField(title, { meterId: settings.meterId, skip: Boolean(dataSource.endpoint) || isPreview });
    const liveValue = useLiveValue(settings.value ?? 0, {
        id: config.id, endpoint: dataSource.endpoint || (report.field ? 'report' : ''), min: 0, max,
    });

    useEffect(() => {
        if (!dataSource.endpoint) return;
        let cancelled = false;
        const token = sessionStorage.getItem('token');

        axios.get(`${BaseURL}${dataSource.endpoint}`, {
            headers: { Authorization: `Token ${token}` },
        })
            .then((res) => {
                if (cancelled) return;
                const data = res.data;
                setFetchedValue(Number(dataSource.field ? data?.[dataSource.field] : data) || 0);
            })
            .catch((err) => console.error('GaugeWidget: failed to fetch', err));

        return () => { cancelled = true; };
    }, [dataSource.endpoint, dataSource.field]);

    const value = dataSource.endpoint
        ? (fetchedValue ?? settings.value ?? 0)
        : (report.field ? (report.latest ?? 0) : liveValue);
    const accent = settings.color || '#4318FF';
    // Text color is independently editable (Value text color) — it falls
    // back to the accent so existing dashboards keep looking the same until
    // someone explicitly picks a different text color from the fill color.
    const textColor = settings.textColor || accent;
    const percent = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0;
    const valueText = `${value} ${settings.unit || ''}`.trim();

    const unit = settings.unit || '';
    const radialOptions = useMemo(() => {
        const isHalf = variant === 'radial-half';
        return {
            chart: { toolbar: { show: false } },
            colors: [accent],
            plotOptions: {
                radialBar: {
                    startAngle: isHalf ? -90 : 0,
                    endAngle: isHalf ? 90 : 360,
                    hollow: { size: '55%' },
                    dataLabels: {
                        name: { show: false },
                        value: {
                            formatter: (val) => `${Math.round((val / 100) * max)} ${unit}`.trim(),
                            fontSize: settings.fontSize ? `${settings.fontSize}px` : '14px',
                            color: settings.textColor || undefined,
                            offsetY: isHalf ? -10 : 0,
                        },
                    },
                },
            },
        };
    }, [variant, accent, max, unit, settings.fontSize, settings.textColor]);

    switch (variant) {
        case 'arc': {
            const r = 80;
            const circumference = Math.PI * r;
            const dashOffset = circumference * (1 - percent);
            return (
                <div className="gauge-widget" title={`${valueText} — ${Math.round(percent * 100)}%`}>
                    <svg viewBox="0 0 200 110" className="arc-meter">
                        <path d="M20,100 A80,80 0 0 1 180,100" fill="none" stroke="#e5e7eb" strokeWidth="14" strokeLinecap="round" />
                        <path
                            d="M20,100 A80,80 0 0 1 180,100"
                            fill="none"
                            stroke={accent}
                            strokeWidth="14"
                            strokeLinecap="round"
                            strokeDasharray={circumference}
                            strokeDashoffset={dashOffset}
                        />
                        <text x="100" y="90" textAnchor="middle" className="arc-meter__text">{valueText}</text>
                    </svg>
                </div>
            );
        }

        case 'ring':
            return (
                <div className="gauge-widget" title={`${valueText} — ${Math.round(percent * 100)}%`}>
                    <CircularProgressbar
                        value={percent * 100}
                        text={valueText}
                        styles={buildStyles({
                            pathColor: accent,
                            textColor: settings.textColor || '#111827',
                            trailColor: '#e5e7eb',
                            textSize: settings.fontSize ? `${settings.fontSize}px` : undefined,
                        })}
                    />
                </div>
            );

        case 'radial-full':
        case 'radial-half':
            return (
                <div className="gauge-widget" title={`${valueText} — ${Math.round(percent * 100)}%`}>
                    <Chart
                        options={radialOptions}
                        series={[Math.round(percent * 100)]}
                        type="radialBar"
                        width={settings.contentWidth || '100%'}
                        height={settings.contentHeight || '100%'}
                    />
                </div>
            );

        case 'bar-horizontal':
            return (
                <div className="gauge-widget" title={`${valueText} — ${Math.round(percent * 100)}%`}>
                    <div className="bar-meter bar-meter--horizontal">
                        <div className="bar-meter__track">
                            <div className="bar-meter__fill" style={{ width: `${percent * 100}%`, backgroundColor: accent }} />
                        </div>
                        <div className="bar-meter__value">{valueText}</div>
                    </div>
                </div>
            );

        case 'bar-vertical':
            return (
                <div className="gauge-widget" title={`${valueText} — ${Math.round(percent * 100)}%`}>
                    <div className="bar-meter bar-meter--vertical">
                        <div className="bar-meter__track bar-meter__track--vertical">
                            <div className="bar-meter__fill bar-meter__fill--vertical" style={{ height: `${percent * 100}%`, backgroundColor: accent }} />
                        </div>
                        <div className="bar-meter__value">{valueText}</div>
                    </div>
                </div>
            );

        case 'digital':
            return (
                <div className="gauge-widget" title={`${valueText} — ${Math.round(percent * 100)}%`}>
                    <div className="digital-meter">
                        <div className="digital-meter__value" style={{ color: textColor }}>{value}<span className="digital-meter__unit">{settings.unit || ''}</span></div>
                        <div className="digital-meter__track">
                            <div className="digital-meter__fill" style={{ width: `${percent * 100}%`, backgroundColor: accent }} />
                        </div>
                    </div>
                </div>
            );

        case 'led': {
            const segCount = 20;
            const litCount = Math.round(percent * segCount);
            return (
                <div className="gauge-widget" title={`${valueText} — ${Math.round(percent * 100)}%`}>
                    <div className="led-meter">
                        <div className="led-meter__segs">
                            {Array.from({ length: segCount }).map((_, i) => (
                                <div
                                    key={i}
                                    className={`led-meter__seg${i < litCount ? ' led-meter__seg--lit' : ''}`}
                                    style={i < litCount ? { backgroundColor: i > segCount * 0.8 ? '#f61612' : accent } : undefined}
                                />
                            ))}
                        </div>
                        <div className="led-meter__value">{valueText}</div>
                    </div>
                </div>
            );
        }

        case 'badge':
            return (
                <div className="gauge-widget" title={`${valueText} — ${Math.round(percent * 100)}%`}>
                    <div className="badge-meter" style={{ backgroundColor: `${accent}1A`, color: textColor, borderColor: accent }}>
                        <span className="badge-meter__value">{valueText}</span>
                        <span className="badge-meter__pct">{Math.round(percent * 100)}%</span>
                    </div>
                </div>
            );

        case 'custom': {
            const trackShape = settings.customTrackShape || 'circle';
            const rawFillStyle = settings.customFillStyle || 'solid';
            const fillStyle = rawFillStyle === 'needle' && trackShape !== 'arc' ? 'solid' : rawFillStyle;
            const valueDisplay = settings.customValueDisplay || 'number';
            const thickness = Math.max(4, Number(settings.customThickness) || 14);
            const orientation = settings.customOrientation || 'horizontal';
            const segCount = 20;
            const litCount = Math.round(percent * segCount);

            const valueNode = (
                <div
                    className={`custom-gauge__value${valueDisplay === 'digital' ? ' custom-gauge__value--digital' : ''}${valueDisplay === 'badge' ? ' custom-gauge__value--badge' : ''}`}
                    style={{ color: textColor, backgroundColor: valueDisplay === 'badge' ? `${accent}1A` : undefined }}
                >
                    {value}{unit && <span className="custom-gauge__unit">{unit}</span>}
                </div>
            );

            let shapeNode;
            if (trackShape === 'circle') {
                const r = 80;
                const circumference = 2 * Math.PI * r;
                const dashOffset = circumference * (1 - percent);
                shapeNode = (
                    <svg viewBox="0 0 200 200" className="custom-gauge__ring">
                        <circle cx="100" cy="100" r={r} fill="none" stroke="#e5e7eb" strokeWidth={thickness} />
                        <circle
                            cx="100"
                            cy="100"
                            r={r}
                            fill="none"
                            stroke={accent}
                            strokeWidth={thickness}
                            strokeLinecap="round"
                            strokeDasharray={circumference}
                            strokeDashoffset={dashOffset}
                            transform="rotate(-90 100 100)"
                        />
                    </svg>
                );
            } else if (trackShape === 'arc') {
                const r = 80;
                const circumference = Math.PI * r;
                const dashOffset = circumference * (1 - percent);
                const needleAngleDeg = 180 + percent * 180;
                const needleRad = (needleAngleDeg * Math.PI) / 180;
                const needleLen = r - thickness / 2 - 4;
                const nx = 100 + needleLen * Math.cos(needleRad);
                const ny = 100 + needleLen * Math.sin(needleRad);
                shapeNode = (
                    <svg viewBox="0 0 200 110" className="custom-gauge__ring">
                        <path d="M20,100 A80,80 0 0 1 180,100" fill="none" stroke="#e5e7eb" strokeWidth={thickness} strokeLinecap="round" />
                        {fillStyle === 'needle' ? (
                            <line x1="100" y1="100" x2={nx} y2={ny} stroke={accent} strokeWidth="3" strokeLinecap="round" />
                        ) : (
                            <path
                                d="M20,100 A80,80 0 0 1 180,100"
                                fill="none"
                                stroke={accent}
                                strokeWidth={thickness}
                                strokeLinecap="round"
                                strokeDasharray={circumference}
                                strokeDashoffset={dashOffset}
                            />
                        )}
                    </svg>
                );
            } else {
                const isVertical = orientation === 'vertical';
                shapeNode = (
                    <div className={`bar-meter${isVertical ? ' bar-meter--vertical' : ''}`}>
                        <div
                            className={`bar-meter__track${isVertical ? ' bar-meter__track--vertical' : ''}`}
                            style={isVertical ? { width: thickness } : { height: thickness }}
                        >
                            {fillStyle === 'segmented' ? (
                                <div className={`led-meter__segs${isVertical ? ' led-meter__segs--vertical' : ''}`}>
                                    {Array.from({ length: segCount }).map((_, i) => (
                                        <div
                                            key={i}
                                            className={`led-meter__seg${i < litCount ? ' led-meter__seg--lit' : ''}`}
                                            style={i < litCount ? { backgroundColor: accent } : undefined}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div
                                    className={`bar-meter__fill${isVertical ? ' bar-meter__fill--vertical' : ''}`}
                                    style={
                                        isVertical
                                            ? { height: `${percent * 100}%`, backgroundColor: accent }
                                            : { width: `${percent * 100}%`, backgroundColor: accent }
                                    }
                                />
                            )}
                        </div>
                    </div>
                );
            }

            return (
                <div className="gauge-widget custom-gauge" title={`${valueText} — ${Math.round(percent * 100)}%`}>
                    {shapeNode}
                    {valueNode}
                </div>
            );
        }

        case 'speedometer':
        default:
            return (
                <div className="gauge-widget" title={`${valueText} — ${Math.round(percent * 100)}%`}>
                    <ReactSpeedometer
                        width={settings.contentWidth || 220}
                        height={settings.contentHeight || 160}
                        minValue={0}
                        maxValue={max}
                        value={value}
                        needleColor={accent}
                        startColor="#4CAF50"
                        segments={8}
                        endColor="#f61612"
                        currentValueText={valueText}
                        textColor={settings.textColor || undefined}
                        valueTextFontSize={settings.fontSize ? `${settings.fontSize}px` : undefined}
                    />
                </div>
            );
    }
};

export default GaugeWidget;
